import React, { useEffect, useState } from 'react';
import { Box, Typography, Paper, CircularProgress, useTheme, useMediaQuery } from '@mui/material';
import { apiRemote } from '@/api/wp-api';
import ActivitiesCompleted from './components/ActivitiesCompleted';
import ActivitiesDue from './components/ActivitiesDue';
import { useAlert } from "@/contexts/AlertContext";

// Thêm interface cho params pagination
interface PaginationParams {
  page: string;
  limit: string;
}

interface IActivityInfo {
  cmid: number;
  modname: string;
  activity_image: string;
  instance: number;
  activity_name: string;
  timecompleted: string;
  activity_url: string;
}

interface ICompletedActivity {
  coursename: string;
  view_url: string;
  categoryname: string;
  cmid: number;
  modname: string;
  activity_image: string;
  activity_name: string;
  timecompleted: string;
  activity_url: string;
}

interface ICompletedActivityResponse {
  totalItems: number;
  currentPage: number;
  totalPages: number;
  completedActivities: ICompletedActivity[];
}

const ITEMS_PER_PAGE = 4;

const CourseActivity = () => {
    const [completedLoading, setCompletedLoading] = useState(false);
    const [dueLoading, setDueLoading] = useState(false);
    const [completedActivities, setCompletedActivities] = useState<any[]>([]);
    const [dueActivities, setDueActivities] = useState<any[]>([]);
    const [completedPagination, setCompletedPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalItems: 0
    });
    const [duePagination, setDuePagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalItems: 0
    });
    const [pageSize, setPageSize] = useState(4);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const alert = useAlert();

    const loadCompletedActivities = async (page: number) => {
        setCompletedLoading(true);
        try {
            const params = {
                page: page.toString(),
                limit: pageSize.toString()
            };

            const response = await apiRemote.getCompletedActivities(params.page, params.limit);
            console.log("response activity complete", response)
            const completedActivitiesData = response.completedActivities.map((item: ICompletedActivity) => ({
                id: item.cmid,
                title: item.activity_name,
                courseName: item.coursename,
                category: item.categoryname,
                completedOn: item.timecompleted,
                activityUrl: item.activity_url,
                activityImage: item.activity_image,
                modname: item.modname
            }));
            setCompletedActivities(completedActivitiesData);
            setCompletedPagination({
                currentPage: response.currentPage,
                totalPages: response.totalPages,
                totalItems: response.totalItems
            });
        } catch (error) {
            alert.error('Error loading completed activities');
            console.error(error);
        } finally {
            setCompletedLoading(false);
        }
    };

    const loadDueActivities = async (page: number) => {
        setDueLoading(true);
        try {
            const params = {
                page: page.toString(),
                limit: pageSize.toString()
            };

            const response = await apiRemote.getDueActivities(params.page, params.limit);
            const dueActivitiesData = response.completedActivities.map((item: ICompletedActivity) => ({
                id: item.cmid,
                title: item.activity_name,
                courseName: item.coursename,
                category: item.categoryname,
                completedOn: item.timecompleted,
                activityUrl: item.activity_url,
                activityImage: item.activity_image,
                modname: item.modname
            }));
            setDueActivities(dueActivitiesData);
            setDuePagination({
                currentPage: response.currentPage,
                totalPages: response.totalPages,
                totalItems: response.totalItems
            });
        } catch (error) {
            alert.error('Error loading due activities');
            console.error(error);
        } finally {
            setDueLoading(false);
        }
    };

    // Initial load
    useEffect(() => {
        loadCompletedActivities(1);
        loadDueActivities(1);
    }, [pageSize]);

    useEffect(() => {
        if(isMobile){
          setPageSize(1)
        }else{
          setPageSize(4)
        }
      }, [isMobile]);

    const handleCompletedPageChange = (page: number) => {
        loadCompletedActivities(page);
    };

    const handleDuePageChange = (page: number) => {
        loadDueActivities(page);
    };

    return (
        <Box sx={{ p: 3, bgcolor: "white", borderRadius: 2, boxShadow: 1 }}>
            {/* Activities Completed Section */}
            <Paper sx={{ mb: 3, p: 2 }}>
                <Typography
                    variant="h5"
                    sx={{
                        mb: 2,
                        color: "rgb(15, 105, 185)",
                        fontSize: '20px',
                        fontWeight: 600
                    }}
                >
                    Activities Completed
                </Typography>
                {completedLoading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                        <CircularProgress />
                    </Box>
                ) : completedActivities.length === 0 ? (
                    <Typography
                        sx={{
                            color: "rgb(102, 102, 102)",
                            fontSize: "1rem",
                            textAlign: "center",
                        }}
                    >
                        No data available
                    </Typography>
                ) : (
                    <ActivitiesCompleted 
                        activities={completedActivities} 
                        loading={completedLoading}
                        pagination={completedPagination}
                        onPageChange={handleCompletedPageChange}
                    />
                )}
            </Paper>

            {/* Activities Due Section */}
            <Paper sx={{ mb: 3, p: 2 }}>
                <Typography
                    variant="h5"
                    sx={{
                        mb: 2,
                        color: "rgb(15, 105, 185)",
                        fontSize: '20px',
                        fontWeight: 600
                    }}
                >
                    Activities Due
                </Typography>
                {dueLoading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                        <CircularProgress />
                    </Box>
                ) : dueActivities.length === 0 ? (
                    <Typography
                        sx={{
                            color: "rgb(102, 102, 102)",
                            fontSize: "1rem",
                            textAlign: "center",
                        }}
                    >
                        No data available
                    </Typography>
                ) : (
                    <ActivitiesDue 
                        activities={dueActivities} 
                        loading={dueLoading}
                        pagination={duePagination}
                        onPageChange={handleDuePageChange}
                    />
                )}
            </Paper>
        </Box>
    );
};

export default CourseActivity;